'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

const TEST_SECONDS = 5;

// CPS verdicts calibrated to the ranges the cps-test community actually uses.
function verdict(cps: number): string {
  if (cps >= 12) return 'Inhuman. Check that your mouse is not double-clicking.';
  if (cps >= 9) return 'Elite clicking, jitter-click territory.';
  if (cps >= 7) return 'Fast, well above average.';
  if (cps >= 5) return 'Solid average.';
  if (cps >= 3) return 'Relaxed pace.';
  return 'Warming up.';
}

export default function BrowserClickSpeedTest() {
  const [state, setState] = useState<'idle' | 'running' | 'done'>('idle');
  const [clicks, setClicks] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TEST_SECONDS);
  const [attempts, setAttempts] = useState<number[]>([]);
  const usageReported = useRef(false);
  const endRef = useRef(0);

  useEffect(() => {
    if (state !== 'running') return;
    const id = window.setInterval(() => {
      const left = Math.max(0, (endRef.current - performance.now()) / 1000);
      setTimeLeft(left);
      if (left <= 0) {
        setState('done');
      }
    }, 50);
    return () => window.clearInterval(id);
  }, [state]);

  useEffect(() => {
    if (state !== 'done') return;
    setAttempts((past) => [clicks / TEST_SECONDS, ...past].slice(0, 10));
    if (!usageReported.current) {
      usageReported.current = true;
      void consumeToolUsage('click-speed-test', '').catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  function handleClick() {
    if (state === 'done') return;
    if (state === 'idle') {
      setClicks(1);
      setTimeLeft(TEST_SECONDS);
      endRef.current = performance.now() + TEST_SECONDS * 1000;
      setState('running');
      return;
    }
    setClicks((count) => count + 1);
  }

  const cps = clicks / TEST_SECONDS;
  const best = attempts.length ? Math.max(...attempts) : 0;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Test your click speed</h2>
          <p>
            Click the pad as fast as you can for {TEST_SECONDS} seconds. The first click starts the
            clock. Everything is measured on your device.
          </p>
        </div>
        <span className="tool-live-badge">CPS</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            aria-label="Click pad"
            onClick={handleClick}
            style={{
              alignItems: 'center',
              background:
                state === 'running' ? 'var(--accent, #2a78d6)' : 'var(--surface-2, #1c2230)',
              border: '2px dashed var(--border, #3a4152)',
              borderRadius: 16,
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              fontSize: '1.5rem',
              gap: '0.5rem',
              justifyContent: 'center',
              minHeight: 260,
              userSelect: 'none',
              width: '100%',
            }}
            type="button"
          >
            {state === 'idle' ? (
              <span>Click to start</span>
            ) : state === 'running' ? (
              <>
                <strong style={{ fontSize: '3rem' }}>{clicks}</strong>
                <span>{timeLeft.toFixed(1)}s left, keep clicking</span>
              </>
            ) : (
              <>
                <strong style={{ fontSize: '2.4rem' }}>{cps.toFixed(1)} CPS</strong>
                <span>
                  {clicks} clicks in {TEST_SECONDS} seconds
                </span>
              </>
            )}
          </button>
          {state === 'done' ? (
            <p aria-live="polite" className="tool-live-panel-note">
              {verdict(cps)}
            </p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          {state === 'done' ? (
            <button
              className="button"
              onClick={() => {
                setClicks(0);
                setState('idle');
              }}
              type="button"
            >
              Try again
            </button>
          ) : null}

          {attempts.length ? (
            <div className="tool-live-stats">
              <div>
                <strong>{best.toFixed(1)} CPS</strong>
                <span>session best of {attempts.length} attempts</span>
              </div>
            </div>
          ) : null}

          <p className="tool-live-panel-note">
            CPS means clicks per second. An average mouse hand lands 5 to 7; sustained 9 and above
            usually means jitter or butterfly clicking technique.
          </p>
        </aside>
      </div>
    </article>
  );
}
